/**
 * Modal Editar Ítems de una cuenta por cobrar (admin/gerente).
 *
 * Agregar productos, quitar líneas o cambiar cantidades de una venta a crédito con
 * saldo pendiente. Los números (nuevo total, saldo, reembolso, stock) los calcula
 * SIEMPRE el backend con `previsualizar: true` — este modal no tiene fórmula propia,
 * para que pantalla y servidor no puedan discrepar. Cualquier cambio invalida la
 * previsualización y obliga a recalcular antes de confirmar.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { ventasAPI, productosAPI } from '../services/api';
import { useApp } from '../context/AppContext';

const METODOS_REEMBOLSO = [
  { value: 'efectivo', label: 'Efectivo', icon: 'bi-cash', color: 'success' },
  { value: 'tarjeta_medianet', label: 'Tarjeta', icon: 'bi-credit-card', color: 'info' },
  { value: 'transferencia', label: 'Transfer.', icon: 'bi-bank', color: 'primary' }
];

const ModalEditarItemsVenta = ({ ventaId, clienteNombre, onClose, onGuardado }) => {
  const { formatCurrency } = useApp();

  const [cargando, setCargando] = useState(true);
  const [venta, setVenta] = useState(null);
  // Existentes: { detalle_id, producto_nombre, producto_codigo, precio_unitario, total, cantidad_original, cantidad, quitar }
  const [lineas, setLineas] = useState([]);
  // Nuevas: { key, producto_id, producto_nombre, producto_codigo, stock, precio_unitario, cantidad, descuento_porcentaje }
  const [nuevas, setNuevas] = useState([]);
  const [buscar, setBuscar] = useState('');
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [motivo, setMotivo] = useState('');

  const [preview, setPreview] = useState(null);
  const [calculando, setCalculando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [facturarSri, setFacturarSri] = useState(null);
  const [reembolso, setReembolso] = useState({ metodo_pago: 'efectivo', detalle: '', referencia: '' });

  // Ref: si el padre pasa una función nueva en cada render, la carga no debe repetirse
  // (borraría lo que el usuario ya editó).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  // Cada cálculo lleva un número: si la lista cambió mientras se calculaba, la respuesta
  // vieja se descarta en vez de mostrarse como si fuera de la lista actual.
  const pedidoRef = useRef(0);

  useEffect(() => {
    let activo = true;
    (async () => {
      try {
        const res = await ventasAPI.getById(ventaId);
        if (!activo) return;
        if (res.data.success) {
          const v = res.data.data;
          setVenta(v);
          setLineas((v.detalle || []).map(d => ({
            detalle_id: d.id,
            producto_nombre: d.producto_nombre,
            producto_codigo: d.producto_codigo,
            precio_unitario: d.precio_unitario,
            total: d.total,
            cantidad_original: d.cantidad,
            cantidad: d.cantidad,
            quitar: false
          })));
        }
      } catch (e) {
        toast.error(e.response?.data?.error || 'No se pudo cargar la venta');
        onCloseRef.current();
      }
      if (activo) setCargando(false);
    })();
    return () => { activo = false; };
  }, [ventaId]);

  // Cualquier cambio en los ítems deja la previsualización vieja: hay que recalcular.
  const invalidar = () => { pedidoRef.current++; setPreview(null); setFacturarSri(null); };

  const buscarProductos = useCallback(async (texto) => {
    if (!texto || texto.length < 2) { setResultados([]); return; }
    setBuscando(true);
    try {
      const res = await productosAPI.getAll({ buscar: texto, limit: 10 });
      if (res.data.success) setResultados(res.data.data || []);
    } catch { /* silencioso */ }
    setBuscando(false);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => buscarProductos(buscar), 300);
    return () => clearTimeout(t);
  }, [buscar, buscarProductos]);

  const agregarProducto = (p) => {
    if (!p || !p.id) return;
    const existente = nuevas.find(n => n.producto_id === p.id);
    if (existente) {
      setNuevas(nuevas.map(n => n.producto_id === p.id ? { ...n, cantidad: n.cantidad + 1 } : n));
    } else {
      setNuevas([...nuevas, {
        key: `n-${p.id}-${Date.now()}`,
        producto_id: p.id,
        producto_nombre: p.nombre,
        producto_codigo: p.codigo,
        stock: parseInt(p.stock_actual) || 0,
        precio_unitario: parseFloat(p.precio_venta) || 0,
        cantidad: 1,
        descuento_porcentaje: 0
      }]);
    }
    setBuscar('');
    setResultados([]);
    invalidar();
  };

  const cambiarLinea = (detalleId, cambios) => {
    setLineas(lineas.map(l => l.detalle_id === detalleId ? { ...l, ...cambios } : l));
    invalidar();
  };

  const cambiarNueva = (key, campo, valor) => {
    setNuevas(nuevas.map(n => {
      if (n.key !== key) return n;
      if (campo === 'cantidad') return { ...n, cantidad: Math.max(1, parseInt(valor) || 1) };
      if (campo === 'descuento_porcentaje') return { ...n, descuento_porcentaje: Math.max(0, Math.min(100, parseFloat(valor) || 0)) };
      if (campo === 'precio_unitario') return { ...n, precio_unitario: Math.max(0, parseFloat(valor) || 0) };
      return n;
    }));
    invalidar();
  };

  const quitarNueva = (key) => { setNuevas(nuevas.filter(n => n.key !== key)); invalidar(); };

  const lineasQueQuedan = lineas.filter(l => !l.quitar);
  const totalLineas = lineasQueQuedan.length + nuevas.length;

  const armarItems = () => [
    ...lineasQueQuedan.map(l => ({ detalle_id: l.detalle_id, cantidad: parseInt(l.cantidad) || 1 })),
    ...nuevas.map(n => ({
      producto_id: n.producto_id, cantidad: n.cantidad,
      precio_unitario: n.precio_unitario, descuento_porcentaje: n.descuento_porcentaje
    }))
  ];

  const calcular = async () => {
    if (totalLineas === 0) { toast.error('La venta debe conservar al menos un ítem. Para eliminarla por completo, anúlela.'); return; }
    const items = armarItems();
    const pedido = ++pedidoRef.current;
    setCalculando(true);
    try {
      const res = await ventasAPI.editarItems(ventaId, { items, previsualizar: true });
      if (pedido !== pedidoRef.current) return;
      if (res.data.sin_cambios) {
        toast.info('No hay cambios en los ítems.');
        setPreview(null);
      } else if (res.data.success) {
        // Se guarda la lista EXACTA que se calculó: es la que se confirma.
        setPreview({ ...res.data.data, items });
        setFacturarSri(null);
      }
    } catch (e) {
      if (pedido !== pedidoRef.current) return;
      toast.error(e.response?.data?.error || 'No se pudo calcular el cambio');
      setPreview(null);
    } finally {
      setCalculando(false);
    }
  };

  const faltaReembolso = preview?.requiere_reembolso && !reembolso.detalle.trim();
  const faltaCajaEfectivo = preview?.requiere_reembolso && reembolso.metodo_pago === 'efectivo' && !preview?.caja_abierta;
  const faltaSri = preview?.requiere_eleccion_sri && facturarSri === null;

  const confirmar = async () => {
    if (!preview) return;
    if (faltaReembolso) { toast.error('Es obligatorio anotar el detalle de lo que se reembolsa.'); return; }
    if (faltaCajaEfectivo) { toast.error('Para reembolsar en efectivo debes tener tu caja abierta. Ábrela o elige otro método.'); return; }
    if (faltaSri) { toast.error('La cuenta queda saldada: elija si se factura CON SRI o SIN SRI.'); return; }

    setGuardando(true);
    try {
      const payload = {
        items: preview.items,
        motivo: motivo.trim() || null,
        // El servidor rechaza (409) si la cuenta ya no da estos números.
        esperado: {
          saldo_anterior: preview.antes.saldo_pendiente,
          total_nuevo: preview.despues.total,
          monto_reembolso: preview.monto_reembolso
        }
      };
      if (preview.requiere_eleccion_sri) payload.facturar_sri = facturarSri;
      if (preview.requiere_reembolso) {
        payload.reembolso = {
          metodo_pago: reembolso.metodo_pago,
          detalle: reembolso.detalle.trim(),
          referencia: reembolso.referencia.trim() || null
        };
      }
      const res = await ventasAPI.editarItems(ventaId, payload);
      if (res.data.sin_cambios) {
        toast.info('Los ítems ya estaban así; no se guardó nada.');
        setPreview(null);
      } else if (res.data.success) {
        const d = res.data.data;
        toast.success(`Venta ${d.codigo} actualizada: total ${formatCurrency(d.despues.total)}, saldo ${formatCurrency(d.despues.saldo_pendiente)}`);
        if (d.reembolso) toast.info(`Reembolso registrado: ${formatCurrency(d.reembolso.monto)}. Entréguelo al cliente.`, { autoClose: 8000 });
        if (d.cuenta_saldada) {
          if (d.facturar_sri === true) {
            if (d.factura_en_proceso) toast.info('Cuenta saldada — se está generando la factura SRI.');
            else toast.warning('Cuenta saldada, pero la facturación automática está APAGADA: la factura NO se emitió. Génerela manualmente.');
          } else if (d.facturar_sri === false) {
            toast.info('Cuenta saldada SIN factura SRI — entregue el comprobante interno.');
          }
        }
        onGuardado(d);
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'No se pudo guardar el cambio');
      // Si el servidor ve algo distinto a la previsualización (otro abono entró entre
      // medio, cambió el stock), obligar a recalcular.
      setPreview(null);
    }
    setGuardando(false);
  };

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 1060 }}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">
              <i className="bi bi-pencil-square me-2"></i>Editar ítems
              {venta && <> — <span className="fw-bold">{venta.codigo}</span></>}
            </h5>
            <button className="btn-close btn-close-white" onClick={onClose} disabled={guardando}></button>
          </div>
          <div className="modal-body">
            {cargando || !venta ? (
              <div className="text-center py-4"><div className="spinner-border"></div></div>
            ) : (
              <>
                <div className="alert alert-light border py-2 mb-3 d-flex justify-content-between flex-wrap gap-2 small">
                  <span><strong>{clienteNombre || venta.cliente_nombre}</strong></span>
                  <span>Total: <strong>{formatCurrency(venta.total)}</strong></span>
                  <span>Abonado: <strong className="text-success">{formatCurrency(venta.monto_abonado)}</strong></span>
                  <span>Saldo: <strong className="text-danger">{formatCurrency(venta.saldo_pendiente)}</strong></span>
                </div>

                {/* Ítems actuales */}
                <h6 className="mb-2"><i className="bi bi-bag me-2"></i>Ítems de la venta</h6>
                <div className="table-responsive mb-3">
                  <table className="table table-sm align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Producto</th>
                        <th className="text-end" style={{ width: 90 }}>Cant.</th>
                        <th className="text-end">P. Unit.</th>
                        <th className="text-end">Total actual</th>
                        <th style={{ width: 44 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineas.map(l => (
                        <tr key={l.detalle_id} className={l.quitar ? 'table-danger' : ''}>
                          <td>
                            <small className="text-muted d-block">{l.producto_codigo}</small>
                            <span className={l.quitar ? 'text-decoration-line-through' : ''}>{l.producto_nombre}</span>
                            {l.quitar && <span className="badge bg-danger ms-2">Se quita</span>}
                            {!l.quitar && Number(l.cantidad) !== Number(l.cantidad_original) && (
                              <span className="badge bg-warning text-dark ms-2">{l.cantidad_original} → {l.cantidad}</span>
                            )}
                          </td>
                          <td className="text-end">
                            <input type="number" className="form-control form-control-sm text-end" min="1" step="1"
                              disabled={l.quitar} onFocus={e => e.target.select()}
                              value={l.cantidad}
                              onChange={e => cambiarLinea(l.detalle_id, { cantidad: Math.max(1, parseInt(e.target.value) || 1) })} />
                          </td>
                          <td className="text-end">{formatCurrency(l.precio_unitario)}</td>
                          <td className="text-end">{formatCurrency(l.total)}</td>
                          <td>
                            {l.quitar ? (
                              <button className="btn btn-sm btn-outline-secondary p-1" title="Deshacer"
                                onClick={() => cambiarLinea(l.detalle_id, { quitar: false })}>
                                <i className="bi bi-arrow-counterclockwise"></i>
                              </button>
                            ) : (
                              <button className="btn btn-sm btn-outline-danger p-1" title="Quitar de la venta"
                                onClick={() => cambiarLinea(l.detalle_id, { quitar: true, cantidad: l.cantidad_original })}>
                                <i className="bi bi-trash"></i>
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {nuevas.map(n => (
                        <tr key={n.key} className="table-success">
                          <td>
                            <small className="text-muted d-block">{n.producto_codigo}</small>
                            {n.producto_nombre} <span className="badge bg-success ms-1">Nuevo</span>
                            <small className={`d-block ${n.cantidad > n.stock ? 'text-danger fw-bold' : 'text-muted'}`}>
                              Stock: {n.stock}{n.cantidad > n.stock && ' — insuficiente (el crédito exige stock)'}
                            </small>
                          </td>
                          <td className="text-end">
                            <input type="number" className="form-control form-control-sm text-end" min="1" step="1"
                              onFocus={e => e.target.select()}
                              value={n.cantidad} onChange={e => cambiarNueva(n.key, 'cantidad', e.target.value)} />
                          </td>
                          <td className="text-end">
                            <input type="number" className={`form-control form-control-sm text-end ${n.precio_unitario > 0 ? '' : 'border-danger'}`} min="0" step="0.01"
                              onFocus={e => e.target.select()}
                              value={n.precio_unitario} onChange={e => cambiarNueva(n.key, 'precio_unitario', e.target.value)} />
                            {n.precio_unitario <= 0 && (
                              <small className="text-danger d-block" style={{ fontSize: '0.7rem', lineHeight: 1.1 }}>
                                Sin precio: se usará el precio de lista
                              </small>
                            )}
                            <div className="input-group input-group-sm mt-1">
                              <input type="number" className="form-control text-end" min="0" max="100" step="1" title="Descuento %"
                                value={n.descuento_porcentaje} onChange={e => cambiarNueva(n.key, 'descuento_porcentaje', e.target.value)} />
                              <span className="input-group-text">% desc.</span>
                            </div>
                          </td>
                          <td className="text-end text-muted small">se calcula</td>
                          <td>
                            <button className="btn btn-sm btn-outline-danger p-1" title="Quitar" onClick={() => quitarNueva(n.key)}>
                              <i className="bi bi-x"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Agregar producto */}
                <div className="mb-3 position-relative">
                  <label className="form-label small fw-bold"><i className="bi bi-plus-circle me-1"></i>Agregar producto</label>
                  <input type="text" className="form-control form-control-sm" placeholder="Buscar por nombre o código (mín. 2 letras)"
                    value={buscar} onChange={e => setBuscar(e.target.value)} />
                  {buscando && <small className="text-muted">Buscando…</small>}
                  {resultados.length > 0 && (
                    <div className="list-group position-absolute w-100 shadow" style={{ zIndex: 5, maxHeight: 240, overflowY: 'auto' }}>
                      {resultados.map(p => (
                        <button key={p.id} type="button" className="list-group-item list-group-item-action py-1 small d-flex justify-content-between"
                          onClick={() => agregarProducto(p)}>
                          <span><span className="fw-bold">{p.codigo}</span> · {p.nombre}</span>
                          <span className={parseInt(p.stock_actual) > 0 ? 'text-muted' : 'text-danger'}>
                            {formatCurrency(p.precio_venta)} · stock {parseInt(p.stock_actual) || 0}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label small">Motivo del cambio <span className="text-muted">(opcional, queda en la auditoría)</span></label>
                  <input type="text" className="form-control form-control-sm" maxLength={500}
                    value={motivo} onChange={e => setMotivo(e.target.value)} />
                </div>

                {/* Resultado calculado por el servidor */}
                {preview && (
                  <div className="border rounded p-3 bg-light">
                    <h6 className="mb-2"><i className="bi bi-calculator me-2"></i>Resultado del cambio</h6>
                    <table className="table table-sm mb-2">
                      <thead><tr><th></th><th className="text-end">Antes</th><th className="text-end">Después</th></tr></thead>
                      <tbody>
                        <tr><td>Total</td><td className="text-end">{formatCurrency(preview.antes.total)}</td><td className="text-end fw-bold">{formatCurrency(preview.despues.total)}</td></tr>
                        <tr><td>Abonado</td><td className="text-end">{formatCurrency(preview.antes.monto_abonado)}</td><td className="text-end">{formatCurrency(preview.despues.monto_abonado)}</td></tr>
                        <tr><td>Saldo</td><td className="text-end">{formatCurrency(preview.antes.saldo_pendiente)}</td><td className="text-end fw-bold text-danger">{formatCurrency(preview.despues.saldo_pendiente)}</td></tr>
                      </tbody>
                    </table>
                    {(preview.cambios.agregados.length > 0 || preview.cambios.quitados.length > 0 || preview.cambios.cantidad.length > 0) && (
                      <ul className="small mb-2 ps-3">
                        {preview.cambios.agregados.map((a, i) => (
                          <li key={`a${i}`} className="text-success">+ {a.producto} × {a.cantidad} a {formatCurrency(a.precio_unitario)} c/u = {formatCurrency(a.total)}</li>
                        ))}
                        {preview.cambios.cantidad.map((c, i) => (
                          <li key={`c${i}`} className="text-warning-emphasis">{c.producto}: {c.cantidad_anterior} → {c.cantidad} = {formatCurrency(c.total)}</li>
                        ))}
                        {preview.cambios.quitados.map((q, i) => (
                          <li key={`q${i}`} className="text-danger">− {q.producto} × {q.cantidad} ({formatCurrency(q.total)})</li>
                        ))}
                      </ul>
                    )}
                    {preview.stock.length > 0 && (
                      <div className="small mb-2">
                        <strong>Inventario:</strong>{' '}
                        {preview.stock.map(s => (
                          <span key={s.producto_id} className={`badge me-1 ${s.movimiento === 'entrada' ? 'bg-success' : 'bg-secondary'}`}>
                            {s.producto}: {s.movimiento === 'entrada' ? '+' : '−'}{s.cantidad} (stock {s.stock_anterior} → {s.stock_posterior})
                          </span>
                        ))}
                      </div>
                    )}

                    {preview.requiere_reembolso && (
                      <div className="border border-danger rounded p-3 mb-2 bg-danger bg-opacity-10">
                        <div className="mb-2">
                          <i className="bi bi-arrow-return-left text-danger me-2"></i>
                          <strong>Hay que reembolsar {formatCurrency(preview.monto_reembolso)} al cliente</strong>
                          <small className="d-block text-muted">El nuevo total queda por debajo de lo que ya abonó.</small>
                        </div>
                        <label className="form-label small fw-bold mb-1">¿Cómo se devuelve?</label>
                        <div className="d-flex gap-2 mb-2">
                          {METODOS_REEMBOLSO.map(m => (
                            <button key={m.value} type="button"
                              className={`btn btn-sm flex-fill ${reembolso.metodo_pago === m.value ? `btn-${m.color}` : `btn-outline-${m.color}`}`}
                              onClick={() => setReembolso({ ...reembolso, metodo_pago: m.value })}>
                              <i className={`bi ${m.icon} me-1`}></i>{m.label}
                            </button>
                          ))}
                        </div>
                        {faltaCajaEfectivo && (
                          <div className="small text-danger mb-2"><i className="bi bi-exclamation-circle me-1"></i>No tienes caja abierta: para devolver en efectivo, ábrela o elige otro método.</div>
                        )}
                        {reembolso.metodo_pago !== 'efectivo' && (
                          <input type="text" className="form-control form-control-sm mb-2" maxLength={100} placeholder="N° de referencia (opcional)"
                            value={reembolso.referencia} onChange={e => setReembolso({ ...reembolso, referencia: e.target.value })} />
                        )}
                        <label className="form-label small fw-bold mb-1">Detalle de lo que se reembolsa <span className="text-danger">*</span></label>
                        <textarea className={`form-control form-control-sm ${faltaReembolso ? 'border-danger' : ''}`} rows={2} maxLength={500}
                          placeholder="Ej: se devuelve el armazón X que el cliente no retiró"
                          value={reembolso.detalle} onChange={e => setReembolso({ ...reembolso, detalle: e.target.value })} />
                        {faltaReembolso && <div className="small text-danger mt-1"><i className="bi bi-exclamation-circle me-1"></i>Obligatorio.</div>}
                      </div>
                    )}

                    {preview.requiere_eleccion_sri && (
                      <div className="border border-warning rounded p-3 bg-warning bg-opacity-10">
                        <strong className="d-block mb-1">Con este cambio la cuenta queda saldada</strong>
                        <small className="text-muted d-block mb-2">Elija cómo se documenta. La factura electrónica no se puede deshacer sin nota de crédito.</small>
                        <div className="d-flex gap-2">
                          <button type="button" className={`btn btn-sm flex-fill ${facturarSri === true ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setFacturarSri(true)}>
                            <i className="bi bi-file-earmark-check me-1"></i>Facturar CON SRI
                          </button>
                          <button type="button" className={`btn btn-sm flex-fill ${facturarSri === false ? 'btn-dark' : 'btn-outline-dark'}`}
                            onClick={() => setFacturarSri(false)}>
                            <i className="bi bi-printer me-1"></i>SIN SRI (comprobante interno)
                          </button>
                        </div>
                        {faltaSri && <div className="small text-danger mt-1"><i className="bi bi-exclamation-circle me-1"></i>Debe elegir una opción para continuar.</div>}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose} disabled={guardando}>Cancelar</button>
            {!preview ? (
              <button className="btn btn-outline-primary" onClick={calcular} disabled={cargando || calculando || totalLineas === 0}>
                {calculando ? <><span className="spinner-border spinner-border-sm me-1"></span>Calculando…</> : <><i className="bi bi-calculator me-1"></i>Calcular cambio</>}
              </button>
            ) : (
              <>
              <button className="btn btn-outline-secondary" onClick={calcular} disabled={guardando || calculando} title="Volver a calcular con los datos actuales">
                <i className="bi bi-arrow-repeat me-1"></i>Recalcular
              </button>
              <button className="btn btn-primary" onClick={confirmar}
                disabled={guardando || calculando || faltaReembolso || faltaCajaEfectivo || faltaSri}>
                {guardando ? <><span className="spinner-border spinner-border-sm me-1"></span>Guardando…</> : <><i className="bi bi-check-lg me-1"></i>Confirmar cambios</>}
              </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalEditarItemsVenta;
